import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMsal } from '@azure/msal-react';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import Drawer from '../../components/ui/Drawer';
import FilterBar from '../../components/FilterBar';
import Toggle from '../../components/Toggle';
import MultiSelect, { MultiSelectOption } from '../../components/MultiSelect';
import EdiBucketPanel from '../../components/EdiBucketPanel';
import EdiBucketDrill, { EdiDrillSelection } from '../../components/EdiBucketDrill';
import {
  EdiBucket,
  bucketKey,
  getEdiBuckets,
  getEdiLcOptions,
} from '../../services/pendingEdiOrders.service';
import { useVisibilityAwareInterval } from '../../hooks/useVisibilityAwareInterval';
import { matchLcToCurrentUser } from '../../utils/matchLcToCurrentUser';

// -----------------------------------------------------------------------------
// Pending EDI Orders (Order Management)
// -----------------------------------------------------------------------------
// Bucket panel (edihandle × Bill To) → drill-down. The drill can render as a right
// Drawer or a full-page swap (drawerView). The drill itself — the single/multi-row
// results, actions, and mapping editors — lives in the shared <EdiBucketDrill> so it
// can be embedded elsewhere (e.g. the LC Dashboard). This page owns the bucket list,
// the Bill To / LC filters, and the drawer-vs-page layout.

const LS_DRAWER = 'pending-edi-drawer-view';
// Background refresh cadence (matches the LC Dashboard counts). Visibility-aware:
// pauses when the tab is hidden and fires immediately on resume.
const REFRESH_MS = 30_000;

// Equality guard so a background refresh only re-renders when data actually changed —
// handing setState a fresh-but-identical array every tick is what caused the flash.
// Buckets come back in a stable order (billtoname, edihandle), so index comparison holds.
const bucketsEqual = (a: EdiBucket[], b: EdiBucket[]): boolean => {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    const x = a[i];
    const y = b[i];
    if (
      x.edihandle !== y.edihandle ||
      x.billto !== y.billto ||
      x.count !== y.count ||
      x.billtoname !== y.billtoname ||
      x.edicsr !== y.edicsr ||
      x.unclaimed !== y.unclaimed
    )
      return false;
  }
  return true;
};

const PendingEdiOrders: React.FC = () => {
  const [buckets, setBuckets] = useState<EdiBucket[]>([]);
  const [bucketsLoading, setBucketsLoading] = useState(true);
  const [bucketsError, setBucketsError] = useState<string | null>(null);

  const [selection, setSelection] = useState<EdiDrillSelection | null>(null);
  const [drawerView, setDrawerView] = useState<boolean>(() => localStorage.getItem(LS_DRAWER) === 'true');

  // Header filters. Bill To narrows the loaded list client-side; the LC filter is
  // sent to the server, because it has to change each bucket's count (see loadBuckets).
  const [selectedBilltos, setSelectedBilltos] = useState<string[]>([]);
  const [selectedLcs, setSelectedLcs] = useState<string[]>([]);

  // The LC filter roster: who works EDI, independent of what is currently filtered.
  // Groups are excluded from the picker here — the page filters by individual LCs, and
  // the backend expands a tilde-joined group value anyway if one is ever passed.
  const [lcRoster, setLcRoster] = useState<{ value: string; label: string }[]>([]);
  useEffect(() => {
    getEdiLcOptions()
      .then((opts) => setLcRoster(opts.filter((o) => !o.isGroup).map((o) => ({ value: o.value, label: o.label }))))
      .catch(() => { /* best-effort — an empty roster just means no LC filter offered */ });
  }, []);

  // Signed-in user, for defaulting the LC filter to "my view" (see the preselect effect).
  const { accounts } = useMsal();
  const currentUser = accounts[0];

  // --- filter options + filtered bucket list ---
  const billtoOptions = useMemo<MultiSelectOption[]>(() => {
    const seen = new Map<string, string>();
    for (const b of buckets) {
      const v = String(b.billto);
      if (!seen.has(v)) seen.set(v, b.billtoname || '(no bill to)');
    }
    return [...seen.entries()]
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [buckets]);

  // Options come from the roster endpoint, NOT from the loaded buckets.
  //
  // Deriving them from `buckets` worked while the response always carried every bucket.
  // Now that the LC filter is applied server-side, the response only contains what the
  // current filter matched — so building options from it would leave Anna as the only
  // name in her own dropdown, with no way to switch to Bob without clearing first.
  const lcOptions = useMemo<MultiSelectOption[]>(
    () => lcRoster.map((o) => ({ value: o.value, label: o.label })),
    [lcRoster]
  );

  // Default the LC filter to the signed-in user once, if they're a CSR on a pending
  // bucket (i.e. they appear in lcOptions). Match on full-name label via
  // matchLcToCurrentUser — the option value is the short csr username, not the email.
  // Ref-guarded so it fires once and never fights a manual clear (incl. Clear Filters).
  const hasAutoSelectedLc = useRef(false);
  useEffect(() => {
    if (hasAutoSelectedLc.current || !lcOptions.length) return;
    const match = matchLcToCurrentUser(lcOptions, currentUser);
    if (match) {
      hasAutoSelectedLc.current = true;
      setSelectedLcs([match.value]);
    }
  }, [lcOptions, currentUser]);

  // Bill To still filters client-side — it is a property of the bucket itself, so
  // hiding a bucket is the whole operation and its count is unaffected.
  //
  // The LC filter CANNOT work that way any more. Ownership is per-order, so filtering
  // to Anna has to change each bucket's COUNT (13, not the handle's 100), which only
  // the server can do. It therefore rides on the request — see loadBuckets — and this
  // memo deliberately no longer touches selectedLcs.
  const filteredBuckets = useMemo(() => {
    const billSet = new Set(selectedBilltos);
    if (!billSet.size) return buckets;
    return buckets.filter((b) => billSet.has(String(b.billto)));
  }, [buckets, selectedBilltos]);

  // Stable key for the LC scope: selectedLcs is a fresh array on every change, and the
  // loader is a dependency of both an effect and the refresh interval.
  const lcScopeKey = selectedLcs.join(',');

  // --- loader (silent = background refresh: no spinner, keep current data on error) ---
  const loadBuckets = useCallback((silent = false) => {
    if (!silent) setBucketsLoading(true);
    return getEdiBuckets(lcScopeKey ? lcScopeKey.split(',') : undefined)
      .then((resp) => {
        // Keep the same array reference when nothing changed → no re-render, no flash.
        setBuckets((prev) => (bucketsEqual(prev, resp.data) ? prev : resp.data));
        setBucketsError(null);
      })
      .catch((e) => {
        if (!silent) setBucketsError(e?.message || 'Failed to load buckets.');
      })
      .finally(() => {
        if (!silent) setBucketsLoading(false);
      });
  }, [lcScopeKey]);

  // Refetches whenever the LC scope changes, because the counts themselves change.
  useEffect(() => {
    loadBuckets(false);
  }, [loadBuckets]);

  // Background refresh of the bucket counts so new moves appear without a manual reload.
  // The open drill refreshes its own rows (see EdiBucketDrill).
  useVisibilityAwareInterval(() => {
    loadBuckets(true);
  }, REFRESH_MS);

  const handleSelect = useCallback(
    (edihandle: string, billto: number) => {
      const b = buckets.find((x) => x.edihandle === edihandle && x.billto === billto);
      const label = b ? `${b.billtoname} — ${edihandle}` : edihandle;
      setSelection({ edihandle, billto, label });
    },
    [buckets]
  );

  const closeDrill = useCallback(() => setSelection(null), []);
  const refreshBuckets = useCallback(() => { loadBuckets(true); }, [loadBuckets]);

  const setDrawer = (v: boolean) => {
    setDrawerView(v);
    localStorage.setItem(LS_DRAWER, String(v));
  };

  const selectedKey = selection ? bucketKey(selection.edihandle, selection.billto) : null;
  const showPageDrill = !!selection && !drawerView;

  return (
    <div className="p-4 space-y-3">
      {/* Header — Bill To / LC filters + Drawer View toggle. The single/multi-row
          view toggle is NOT here; it lives in the drill (EdiBucketDrill). */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h1 className="text-xl font-bold text-body">Pending EDI Orders</h1>
        <FilterBar>
          <MultiSelect
            options={billtoOptions}
            value={selectedBilltos}
            onChange={setSelectedBilltos}
            placeholder="All Bill Tos"
            showClearOption
            clearOptionLabel="All Bill Tos"
            enableSearch
            searchPlaceholder="Search Bill To…"
            size="sm"
            usePortal
            className="min-w-[200px]"
          />
          <MultiSelect
            options={lcOptions}
            value={selectedLcs}
            onChange={setSelectedLcs}
            placeholder="All LCs"
            showClearOption
            clearOptionLabel="All LCs"
            enableSearch
            searchPlaceholder="Search LC…"
            size="sm"
            usePortal
            className="min-w-[160px]"
          />
          {(selectedBilltos.length > 0 || selectedLcs.length > 0) && (
            <button
              type="button"
              onClick={() => { setSelectedBilltos([]); setSelectedLcs([]); }}
              className="text-xs text-blue-600 dark:text-blue-400 hover:underline whitespace-nowrap"
            >
              Clear Filters
            </button>
          )}
          <FilterBar.Group label="Drawer view">
            <Toggle checked={drawerView} onChange={setDrawer} />
          </FilterBar.Group>
        </FilterBar>
      </div>

      {bucketsError && <div className="text-sm text-red-600 dark:text-red-400">{bucketsError}</div>}

      {/* Page-mode drill swaps out the panel; drawer-mode keeps the panel and overlays it. */}
      {showPageDrill ? (
        <EdiBucketDrill
          selection={selection}
          onActionApplied={refreshBuckets}
          lcUsernames={selectedLcs}
          header={
            <>
              <button
                type="button"
                onClick={closeDrill}
                className="flex items-center gap-1 text-sm text-blue-600 dark:text-blue-400 hover:underline"
              >
                <ArrowLeftIcon className="w-4 h-4" /> Back to buckets
              </button>
              <span className="text-sm font-semibold text-gray-800 dark:text-gray-200">{selection?.label}</span>
            </>
          }
        />
      ) : (
        <EdiBucketPanel
          buckets={filteredBuckets}
          loading={bucketsLoading}
          onSelect={handleSelect}
          selectedKey={selectedKey}
        />
      )}

      {/* Drawer-mode drill. noBackdrop → a z-[100] portal (not a top-layer <dialog>) so
          the drill's EDI Info / reason / confirm modals layer on top instead of hiding
          underneath (same reason the LC Dashboard drill drawers use noBackdrop). */}
      <Drawer
        isOpen={!!selection && drawerView}
        onClose={closeDrill}
        position="right"
        width="80vw"
        title={selection?.label || 'Results'}
        noBackdrop
      >
        {selection && drawerView && (
          <div className="p-3">
            <EdiBucketDrill selection={selection} onActionApplied={refreshBuckets} lcUsernames={selectedLcs} />
          </div>
        )}
      </Drawer>
    </div>
  );
};

export default PendingEdiOrders;
